import React from 'react';
/* eslint-disable react/no-array-index-key */
import tw from 'twin.macro';
import {
  bgColors,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import { CheckIcon as Check } from '@heroicons/react/20/solid';
import styled from '@emotion/styled';
import { allPlainColors, colorsValue } from '@teetsh/app/src/utils/constants';
import { Color } from '@teetsh/app/src/types/colors';
import { intensities, tagColors } from 'styles/userConstants';

export function getPaletteColorOptions(colorPalette: ColorPalette): {
  options: Option[];
  nbColumns: number;
} {
  if (colorPalette === 'old') {
    const options = [
      // empty is used to leave a space
      ...['empty', 'empty', 'empty', 'empty', 'black', 'white'],
      ...colorsValue.map(i => `gray-${i}`),
      ...allPlainColors.flatMap(c => colorsValue.map(i => `${c}-${i}`)),
    ].map(color => ({
      color: color as Color,
    }));

    return {
      options,
      nbColumns: colorsValue.length,
    };
  }

  const options = [
    ...tagColors.flatMap(c => intensities.map(i => `${c}-${i}`)),
  ].map(color => ({
    color: color as Color,
  }));
  return {
    options,
    nbColumns: intensities.length,
  };
}
export interface Option {
  color: Color | 'empty';
}
export type ColorPalette = 'old' | 'new';

type CommonProps = {
  value: string;
  onChange: (v: Color) => void;
  buttonRef?: any;
};
type PropsWithColorPalette = CommonProps & {
  colorPalette: ColorPalette;
  options?: never; // Ensure options is not provided when colorPalette is
};

type PropsWithOptions = CommonProps & {
  colorPalette?: never; // Ensure colorPalette is not provided when options is
  options: Option[];
};

// Union type that allows either colorPalette or options, but not both
type Props = PropsWithColorPalette | PropsWithOptions;

const Grid = styled.div<{ nbColumns: number }>`
  grid-template-columns: ${props => `repeat(${props.nbColumns}, 1fr)`};
`;

export function ColorsList(props: Props) {
  const { value, onChange, buttonRef } = props;

  // Determine if we're using options or colorPalette
  const { options, nbColumns } = props.options
    ? {
        options: props.options,
        nbColumns: props.options.length,
      }
    : getPaletteColorOptions(props.colorPalette);

  return (
    <Grid nbColumns={nbColumns} tw="grid gap-2">
      {options.map((col, index) => {
        if (col.color === 'empty') {
          return <div key={index} tw="invisible" />;
        }
        return (
          <button
            type="button"
            data-testid={`button-${col.color}`}
            css={[
              tw`h-9 min-w-9 rounded-lg border border-neutral-moderate border-opacity-40 hover:bg-opacity-70 outline-none!`,
              getTextColorFromBackgroundColor(col.color),
              bgColors(col.color),
              value === col.color && tw`border-2 border-neutral-intense`,
            ]}
            key={col.color}
            onClick={() => {
              onChange(col.color as Color);
              if (buttonRef?.current) {
                (buttonRef.current as any).click();
              }
            }}
          >
            {value === col.color ? (
              <Check tw="h-5 w-5 inline text-neutral-moderate" />
            ) : (
              <div tw="h-5 w-5" />
            )}
          </button>
        );
      })}
    </Grid>
  );
}
